import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../api/axios';
import toast from 'react-hot-toast';

/* ── Load Razorpay script ── */
function loadRazorpayScript() {
    return new Promise((resolve) => {
        if (window.Razorpay) return resolve(true);
        const s = document.createElement('script');
        s.src = 'https://checkout.razorpay.com/v1/checkout.js';
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.body.appendChild(s);
    });
}

const INCLUDED_DEFAULT = [
    { icon: '🔢', text: 'Lo Shu Grid — all 9 energy planes' },
    { icon: '⚡', text: 'Rajyog & Golden Yog detection (16 combos)' },
    { icon: '🪐', text: 'Vedic Navagraha Planetary Grid' },
    { icon: '🔮', text: 'Mulank & Bhagyank' },
    { icon: '💊', text: 'Personalised Missing Number Remedies' },
    { icon: '🎨', text: 'Lucky & Enemy Numbers & Colours' },
    { icon: '📅', text: 'Vimshottari Dasha Chart (120-yr timeline)' },
    { icon: '🌐', text: 'Reports in 18+ languages + PDF & WhatsApp' },
    { icon: '♾️', text: 'Lifetime access — zero recurring fees' },
];

export default function Checkout() {
    const { user, isSubscribed, refreshSubscription } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    /* ── Checkout Config ── */
    const checkoutType = searchParams.get('type') || 'subscription'; // subscription | bracelet | common | bulk
    const braceletId = searchParams.get('id');
    const commonId = searchParams.get('id');
    const bulkBraceletIds = searchParams.get('ids') || '';
    const bulkCommonIds = searchParams.get('commonIds') || '';
    // For single-product checkout: Dashboard passes the computed variant price in the URL
    const urlPrice = searchParams.get('price') ? Number(searchParams.get('price')) : null;
    // For single-product checkout, Dashboard passes the selected variant in ?variant param
    const urlVariant = searchParams.get('variant') ? decodeURIComponent(searchParams.get('variant')) : null;

    const isProductCheckout = ['bracelet', 'common', 'bulk'].includes(checkoutType);

    /* ── Variant price resolver (mirrors Dashboard calcVariantPrice) ── */
    const resolveVariantPrice = (item, variantStr) => {
        if (!variantStr) return { price: item.price, originalPrice: item.originalPrice || item.price };
        const isBracelet = !!item.number;
        if (isBracelet) {
            if (variantStr.includes('21 Steps - Premium')) {
                return {
                    price: item.price21Steps || item.price,
                    originalPrice: item.originalPrice21Steps || item.originalPrice || item.price
                };
            }
            if (variantStr.includes('11 Steps - Advanced')) {
                return {
                    price: item.price11Steps || item.price,
                    originalPrice: item.originalPrice11Steps || item.originalPrice || item.price
                };
            }
            // 3 Steps - Basic → base price
            return { price: item.price, originalPrice: item.originalPrice || item.price };
        } else {
            // Common product: match variant by name in variants array
            if (item.variants && item.variants.length > 0) {
                const matched = item.variants.find(v => v.name === variantStr);
                if (matched) {
                    return {
                        price: matched.price,
                        originalPrice: matched.originalPrice || matched.price
                    };
                }
            }
            return { price: item.price, originalPrice: item.originalPrice || item.price };
        }
    };

    // Redirect if already subscribed (Only for subscription checkout)
    useEffect(() => {
        if (!isProductCheckout && isSubscribed && isSubscribed()) {
            navigate('/dashboard', { replace: true });
        }
    }, [isSubscribed, navigate, isProductCheckout]);

    /* ── State ── */
    const [checkoutItems, setCheckoutItems] = useState([]);
    const [pricing, setPricing] = useState({
        toolName: 'Loshu Grid Calculator',
        toolPrice: 1500,
        toolOriginalPrice: 3000,
        offerLabel: '50% OFF'
    });
    const [loading, setLoading] = useState(true);
    const [paying, setPaying] = useState(false);
    const [orderPlaced, setOrderPlaced] = useState(null); // stores order details on success

    /* ── Shipping Address Form ── */
    const [shippingForm, setShippingForm] = useState({
        fullName: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        postalCode: '',
        phone: ''
    });

    useEffect(() => {
        if (user) {
            setShippingForm(prev => ({
                ...prev,
                fullName: `${user.firstName} ${user.lastName || ''}`.trim(),
                phone: user.phone || ''
            }));
        }
    }, [user]);

    /* ── Fetch Products Details ── */
    useEffect(() => {
        const fetchDetails = async () => {
            try {
                setLoading(true);
                // 1. Fetch Subscription Pricing info
                const { data: priceData } = await API.get('/payment/price');
                if (priceData.success) {
                    setPricing(priceData);
                }

                if (isProductCheckout) {
                    // 2. Fetch all products to match
                    const [braceletsRes, commonProductsRes] = await Promise.all([
                        API.get('/bracelets').catch(() => ({ data: { success: false } })),
                        API.get('/common-products').catch(() => ({ data: { success: false } }))
                    ]);

                    const bracelets = braceletsRes.data.success ? braceletsRes.data.data : [];
                    const commonProducts = commonProductsRes.data.success ? commonProductsRes.data.data : [];

                    let selectedItems = [];

                    if (checkoutType === 'bracelet' && braceletId) {
                        const product = bracelets.find(b => b._id === braceletId);
                        if (product) selectedItems.push(product);
                    } else if (checkoutType === 'common' && commonId) {
                        const product = commonProducts.find(c => c._id === commonId);
                        if (product) selectedItems.push(product);
                    } else if (checkoutType === 'bulk') {
                        const bIds = bulkBraceletIds.split(',').filter(Boolean);
                        const cIds = bulkCommonIds.split(',').filter(Boolean);

                        bIds.forEach(id => {
                            const product = bracelets.find(b => b._id === id);
                            if (product) selectedItems.push(product);
                        });
                        cIds.forEach(id => {
                            const product = commonProducts.find(c => c._id === id);
                            if (product) selectedItems.push(product);
                        });
                    }

                    if (selectedItems.length === 0) {
                        toast.error('Could not load recommended products.');
                        navigate('/dashboard');
                        return;
                    }

                    // Format items
                    // variants URL format: "productId:encodedVariantStr" or "productId:encodedVariantStr:qty"
                    const variantsParam = searchParams.get('variants') || '';
                    const variantMap = {};  // { productId: variantStr }
                    const qtyMap = {};      // { productId: quantity }
                    variantsParam.split(',').forEach(part => {
                        if (!part) return;
                        // Split on first colon only to get id, then rest
                        const firstColon = part.indexOf(':');
                        if (firstColon === -1) return;
                        const id = part.slice(0, firstColon);
                        const rest = part.slice(firstColon + 1);
                        // Check if rest ends with ':number' (qty suffix from cart)
                        const lastColon = rest.lastIndexOf(':');
                        let variantEncoded = rest;
                        let qty = 1;
                        if (lastColon !== -1) {
                            const possibleQty = rest.slice(lastColon + 1);
                            if (/^\d+$/.test(possibleQty)) {
                                qty = parseInt(possibleQty, 10) || 1;
                                variantEncoded = rest.slice(0, lastColon);
                            }
                        }
                        if (id) {
                            variantMap[id] = decodeURIComponent(variantEncoded);
                            qtyMap[id] = qty;
                        }
                    });

                    // For single-product checkout, inject the ?variant param into variantMap
                    if (urlVariant && selectedItems.length === 1) {
                        variantMap[selectedItems[0]._id] = urlVariant;
                    }

                    const formatted = selectedItems.map((item) => {
                        // Use variant from map; no hardcoded fallback — empty string resolves to base price
                        const variantStr = variantMap[item._id] || '';
                        const quantity = qtyMap[item._id] || 1;

                        // Always resolve price from live product data using variant — dynamic and consistent
                        const resolved = resolveVariantPrice(item, variantStr);
                        return {
                            productType: item.number ? 'BraceletProduct' : 'CommonProduct',
                            productId: item._id,
                            name: item.name,
                            price: resolved.price,
                            originalPrice: resolved.originalPrice,
                            imageUrl: item.imageUrl,
                            quantity,
                            variant: variantStr
                        };
                    });

                    setCheckoutItems(formatted);
                }
            } catch (err) {
                console.error(err);
                toast.error('Error preparing checkout details.');
            } finally {
                setLoading(false);
            }
        };

        fetchDetails();
    }, [checkoutType, braceletId, commonId, bulkBraceletIds, bulkCommonIds, isProductCheckout, navigate]);

    /* ── Coupon ── */
    const [couponInput, setCouponInput] = useState('');
    const [couponLoading, setCouponLoading] = useState(false);
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [couponError, setCouponError] = useState('');

    const basePrice = isProductCheckout
        ? checkoutItems.reduce((sum, item) => sum + item.price * item.quantity, 0)
        : pricing.toolPrice;

    const baseOriginalPrice = isProductCheckout
        ? checkoutItems.reduce((sum, item) => sum + item.originalPrice * item.quantity, 0)
        : pricing.toolOriginalPrice;

    const effectivePrice = appliedCoupon
        ? (appliedCoupon.type === 'percent'
            ? Math.max(0, Math.round(basePrice * (1 - appliedCoupon.value / 100)))
            : Math.max(0, basePrice - appliedCoupon.value))
        : basePrice;

    const savings = baseOriginalPrice - effectivePrice;
    const discountPct = baseOriginalPrice > 0 ? Math.round((savings / baseOriginalPrice) * 100) : 0;

    const handleApplyCoupon = async () => {
        if (!couponInput.trim()) return;
        setCouponLoading(true); setCouponError(''); setAppliedCoupon(null);
        try {
            const { data } = await API.post('/payment/apply-coupon', { code: couponInput.trim() });
            if (data.success) {
                setAppliedCoupon({
                    code: data.coupon.code,
                    type: data.coupon.type,
                    value: data.coupon.value,
                    savings: basePrice - (data.coupon.type === 'percent' ? Math.max(0, Math.round(basePrice * (1 - data.coupon.value / 100))) : Math.max(0, basePrice - data.coupon.value))
                });
                toast.success(`✅ Coupon applied!`);
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Invalid coupon.';
            setCouponError(msg); toast.error(msg);
        } finally { setCouponLoading(false); }
    };

    const handleRemoveCoupon = () => { setAppliedCoupon(null); setCouponInput(''); setCouponError(''); };

    const handleRemoveProduct = (indexToRemove) => {
        const updated = checkoutItems.filter((_, idx) => idx !== indexToRemove);
        setCheckoutItems(updated);
        toast.success('Removed item from your checkout list.');
        if (updated.length === 0) {
            navigate('/dashboard');
        }
    };

    /* ── Pay ── */
    const handlePay = async () => {
        if (!user) { toast.error('Please log in first.'); navigate('/login'); return; }

        // Shipping validation for physical products
        if (isProductCheckout) {
            const { fullName, addressLine1, city, state, postalCode, phone } = shippingForm;
            if (!fullName || !addressLine1 || !city || !state || !postalCode || !phone) {
                toast.error('Please fill in all shipping details.');
                return;
            }
            if (phone.length < 10) {
                toast.error('Please enter a valid phone number.');
                return;
            }
        }

        const loaded = await loadRazorpayScript();
        if (!loaded) { toast.error('Could not load payment gateway. Check your internet.'); return; }

        setPaying(true);
        try {
            let orderData;
            if (isProductCheckout) {
                // Product order creation
                const { data } = await API.post('/payment/create-product-order', {
                    items: checkoutItems.map(item => ({
                        productType: item.productType,
                        productId: item.productId,
                        name: item.name,
                        price: item.price,
                        quantity: item.quantity,
                        variant: item.variant || ''
                    })),
                    shippingAddress: shippingForm,
                    couponCode: appliedCoupon?.code || null
                });
                if (!data.success) throw new Error(data.message);
                orderData = data;
            } else {
                // Subscription order creation
                const { data } = await API.post('/payment/create-order', { couponCode: appliedCoupon?.code || null });
                if (!data.success) throw new Error(data.message);
                orderData = data;
            }

            if (orderData.isFreeOrder) {
                if (!isProductCheckout) {
                    await refreshSubscription();
                    toast.success('🎉 Payment successful! Welcome aboard!');
                    navigate('/dashboard');
                } else {
                    toast.success('🎉 Order placed successfully!');
                    setOrderPlaced(orderData.order);
                }
                setPaying(false);
                return;
            }

            const options = {
                key: orderData.key,
                amount: orderData.order.amount,
                currency: orderData.order.currency,
                name: "Aakriti's Healverse",
                description: isProductCheckout ? 'Crystals & Charging Plates' : `${orderData.toolName} — Lifetime Access`,
                image: 'https://aakritishealverse.com/cdn/shop/files/Healverse_logo_1.png',
                order_id: orderData.order.id,
                prefill: { name: orderData.user.name, email: orderData.user.email, contact: orderData.user.phone },
                theme: { color: '#cead80' },
                handler: async (response) => {
                    try {
                        let verifyRes;
                        if (isProductCheckout) {
                            verifyRes = await API.post('/payment/verify-product-order', {
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature,
                                items: orderData.validatedItems,
                                totalAmount: orderData.totalAmount,
                                discountAmount: orderData.discountAmount,
                                finalAmount: orderData.finalAmount,
                                couponCode: appliedCoupon?.code || null,
                                shippingAddress: shippingForm
                            });
                        } else {
                            verifyRes = await API.post('/payment/verify', {
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature,
                                couponCode: appliedCoupon?.code || null,
                                finalPrice: orderData.finalPrice,
                            });
                        }

                        if (verifyRes.data.success) {
                            if (!isProductCheckout) {
                                await refreshSubscription();
                                toast.success('🎉 Payment successful! Welcome aboard!');
                                navigate('/dashboard');
                            } else {
                                toast.success('🎉 Order placed successfully!');
                                setOrderPlaced(verifyRes.data.order);
                            }
                        } else {
                            toast.error('Verification failed. Please contact support.');
                        }
                    } catch (err) {
                        console.error(err);
                        toast.error('Verification error. Contact support with your payment ID.');
                    } finally {
                        setPaying(false);
                    }
                },
                modal: {
                    ondismiss: () => {
                        setPaying(false);
                        toast('Payment cancelled.', { icon: '⚠️' });
                    }
                },
            };
            new window.Razorpay(options).open();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || 'Could not start payment.');
            setPaying(false);
        }
    };

    /* ── Success Screen ── */
    if (orderPlaced) {
        return (
            <div style={{
                minHeight: '100vh', background: 'linear-gradient(135deg, #faf6f0 0%, #f5efe6 50%, #fdf9f5 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '100px 20px 40px',
                fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
            }}>
                <div style={{
                    background: '#fff', borderRadius: 24, padding: '40px 32px',
                    border: '2px solid rgba(206,173,128,0.3)',
                    boxShadow: '0 12px 50px rgba(185,140,80,0.15)',
                    maxWidth: 600, width: '100%', textAlign: 'center',
                    animation: 'fadeUp 0.5s ease'
                }}>
                    <style>{`
                        @keyframes fadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
                        @keyframes scaleIn { from { transform: scale(0.8); opacity: 0; } to { transform: scale(1); opacity: 1; } }
                        .check-icon { font-size: 64px; color: #16a34a; margin-bottom: 16px; animation: scaleIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275); }
                    `}</style>
                    <div className="check-icon">🎉</div>
                    <h1 style={{ fontSize: 28, fontWeight: 900, color: '#1a0a00', marginBottom: 12 }}>
                        Order Placed Successfully!
                    </h1>
                    <p style={{ fontSize: 15, color: '#64748b', marginBottom: 28 }}>
                        Thank you for your purchase! Your order is paid and is being prepared for shipping.
                    </p>

                    {/* Receipt details */}
                    <div style={{
                        background: '#fafafa', borderRadius: 16, border: '1.5px solid #f1f5f9',
                        padding: 24, textAlign: 'left', marginBottom: 28
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                            <span style={{ fontSize: 13, color: '#94a3b8', fontWeight: 600 }}>Order ID</span>
                            <span style={{ fontSize: 13, color: '#1a0a00', fontWeight: 700, fontFamily: 'monospace' }}>
                                #{orderPlaced._id.slice(-10).toUpperCase()}
                            </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                            <span style={{ fontSize: 13, color: '#94a3b8', fontWeight: 600 }}>Transaction ID</span>
                            <span style={{ fontSize: 13, color: '#1a0a00', fontWeight: 700, fontFamily: 'monospace' }}>
                                {orderPlaced.razorpayPaymentId}
                            </span>
                        </div>
                        <div style={{ height: 1, background: '#f1f5f9', margin: '14px 0' }} />

                        <div style={{ fontSize: 13, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>Items Ordered</div>
                        {orderPlaced.items.map((item, idx) => (
                            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14 }}>
                                <span style={{ color: '#475569', fontWeight: 600 }}>
                                    {item.name}
                                    {item.variant && <span style={{ color: '#cead80', fontSize: 11.5, marginLeft: 6 }}>({item.variant})</span>}
                                    <span style={{ color: '#94a3b8', marginLeft: 4 }}>x{item.quantity}</span>
                                </span>
                                <span style={{ color: '#1a0a00', fontWeight: 700 }}>₹{item.price.toLocaleString('en-IN')}</span>
                            </div>
                        ))}
                        <div style={{ height: 1, background: '#f1f5f9', margin: '14px 0' }} />

                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16 }}>
                            <span style={{ fontWeight: 800, color: '#1a0a00' }}>Amount Paid</span>
                            <span style={{ fontWeight: 900, color: '#cead80' }}>₹{orderPlaced.finalAmount.toLocaleString('en-IN')}</span>
                        </div>
                        <div style={{ height: 1, background: '#f1f5f9', margin: '14px 0' }} />

                        <div style={{ fontSize: 13, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>Shipping Address</div>
                        <div style={{ fontSize: 13.5, color: '#475569', lineHeight: 1.5, fontWeight: 500 }}>
                            <div style={{ fontWeight: 700, color: '#1a0a00', marginBottom: 3 }}>{orderPlaced.shippingAddress.fullName}</div>
                            {orderPlaced.shippingAddress.addressLine1}
                            {orderPlaced.shippingAddress.addressLine2 && `, ${orderPlaced.shippingAddress.addressLine2}`}
                            <div>{orderPlaced.shippingAddress.city}, {orderPlaced.shippingAddress.state} - {orderPlaced.shippingAddress.postalCode}</div>
                            <div style={{ marginTop: 4 }}>📞 {orderPlaced.shippingAddress.phone}</div>
                        </div>
                    </div>

                    <button
                        onClick={() => navigate('/dashboard')}
                        style={{
                            background: 'linear-gradient(135deg, #a07e4a, #cead80)',
                            color: '#1a0a00',
                            fontWeight: 900,
                            padding: '14px 32px',
                            border: 'none',
                            borderRadius: 12,
                            fontSize: 15,
                            cursor: 'pointer',
                            boxShadow: '0 8px 24px rgba(206,173,128,0.4)',
                            transition: 'all 0.25s',
                        }}
                    >
                        Back to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    /* ── Loading state ── */
    if (loading) {
        return (
            <div style={{
                minHeight: '100vh', background: '#faf6f0',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}>
                <div style={{ fontSize: 18, color: '#cead80', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ display: 'inline-block', animation: 'shimmer 1s infinite' }}>💎</span> Loading checkout details...
                </div>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh', background: 'linear-gradient(135deg, #faf6f0 0%, #f5efe6 50%, #fdf9f5 100%)',
            paddingTop: 80, paddingBottom: 60,
            fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
            overflowX: 'hidden',
        }}>
            <style>{`
                @keyframes fadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes shimmer { 0%,100%{opacity:1} 50%{opacity:0.6} }
                .co-input:focus { outline: none; border-color: #cead80 !important; box-shadow: 0 0 0 3px rgba(206,173,128,0.15); }
                .co-btn-pay:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 12px 40px rgba(206,173,128,0.5) !important; }
                .co-btn-pay:active:not(:disabled) { transform: translateY(0); }
                .ship-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
                /* Two-column checkout grid — collapses on mobile */
                .co-grid { display: grid; grid-template-columns: minmax(0,1fr) 380px; gap: 28px; align-items: start; }
                @media (max-width: 768px) {
                    .co-grid { grid-template-columns: 1fr; }
                    .co-summary-sticky { position: static !important; }
                }
                @media (max-width: 550px) {
                    .ship-grid { grid-template-columns: 1fr; }
                    .co-summary-price { font-size: 22px !important; }
                    .co-savings-amount { font-size: 13px !important; }
                }
            `}</style>

            <div style={{ maxWidth: 1060, margin: '0 auto', padding: '0 20px', animation: 'fadeUp 0.4s ease' }}>

                {/* ── Breadcrumb ── */}
                <div style={{ marginBottom: 28, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#94a3b8' }}>
                    <Link to="/" style={{ color: '#cead80', textDecoration: 'none', fontWeight: 600 }}>Home</Link>
                    <span>/</span>
                    <span style={{ color: '#475569', fontWeight: 600 }}>Checkout</span>
                </div>

                <h1 style={{ fontSize: 'clamp(22px,3.5vw,32px)', fontWeight: 900, color: '#1a0a00', marginBottom: 32 }}>
                    🛒 Complete Your Order
                </h1>

                <div className="co-grid">

                    {/* ════════════ LEFT — Order + Shipping + Coupon ════════════ */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

                        {/* Product Card / Order details */}
                        <div style={{
                            background: '#fff', borderRadius: 20, padding: '28px 28px',
                            border: '1.5px solid rgba(206,173,128,0.25)',
                            boxShadow: '0 4px 24px rgba(185,140,80,0.08)',
                        }}>
                            <h2 style={{ fontSize: 14, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 20 }}>
                                📦 Order Details
                            </h2>

                            {!isProductCheckout ? (
                                /* Subscription Detail */
                                <div style={{ display: 'flex', alignItems: 'center', gap: 18, paddingBottom: 20, borderBottom: '1.5px solid #f1f5f9' }}>
                                    <div style={{
                                        width: 68, height: 68, borderRadius: 16, flexShrink: 0,
                                        background: 'linear-gradient(135deg, rgba(206,173,128,0.18), rgba(206,173,128,0.06))',
                                        border: '1.5px solid rgba(206,173,128,0.3)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: 30,
                                    }}>🔢</div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 800, fontSize: 17, color: '#1a0a00', marginBottom: 4 }}>
                                            {pricing.toolName}
                                        </div>
                                        <div style={{ fontSize: 13, color: '#64748b' }}>Lifetime Access — One-time payment</div>
                                        <div style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 6,
                                            background: '#dcfce7', border: '1px solid #bbf7d0',
                                            borderRadius: 50, padding: '3px 10px',
                                            fontSize: 11, fontWeight: 700, color: '#16a34a',
                                        }}>✅ Digital Product · Instant Access</div>
                                    </div>
                                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                        <div style={{ fontSize: 22, fontWeight: 900, color: '#cead80' }}>
                                            ₹{pricing.toolOriginalPrice.toLocaleString('en-IN')}
                                        </div>
                                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>MRP</div>
                                    </div>
                                </div>
                            ) : (
                                /* Products Details */
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingBottom: 20, borderBottom: '1.5px solid #f1f5f9' }}>
                                    {checkoutItems.map((item, index) => (
                                        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
                                            {item.imageUrl ? (
                                                <img src={item.imageUrl} alt={item.name}
                                                    style={{ width: 68, height: 68, borderRadius: 16, objectFit: 'cover', border: '1px solid rgba(206,173,128,0.2)' }}
                                                />
                                            ) : (
                                                <div style={{
                                                    width: 68, height: 68, borderRadius: 16, flexShrink: 0,
                                                    background: 'linear-gradient(135deg, rgba(206,173,128,0.18), rgba(206,173,128,0.06))',
                                                    border: '1.5px solid rgba(206,173,128,0.3)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: 30,
                                                }}>💎</div>
                                            )}
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontWeight: 800, fontSize: 16, color: '#1a0a00', marginBottom: 4 }}>
                                                    {item.name}
                                                </div>
                                                {item.variant && (
                                                    <div style={{ fontSize: 11.5, fontWeight: 700, color: '#cead80', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                                                        ✨ Custom: {item.variant}
                                                    </div>
                                                )}
                                                <div style={{ fontSize: 12.5, color: '#64748b' }}>
                                                    {item.productType === 'BraceletProduct' ? 'Sacred Numerology Bracelet' : 'Essential Healing Plate'}
                                                </div>
                                                <div style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 6,
                                                    background: '#e0f2fe', border: '1px solid #bae6fd',
                                                    borderRadius: 50, padding: '3px 10px',
                                                    fontSize: 10.5, fontWeight: 700, color: '#0369a1',
                                                }}>🚚 Fast Shipping · 100% Original</div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
                                                <div style={{ textAlign: 'right' }}>
                                                    <div style={{ fontSize: 19, fontWeight: 900, color: '#cead80' }}>
                                                        ₹{item.price.toLocaleString('en-IN')}
                                                    </div>
                                                    {item.originalPrice && item.originalPrice > item.price && (
                                                        <div style={{ fontSize: 11, color: '#94a3b8', textDecoration: 'line-through', marginTop: 2 }}>
                                                            ₹{item.originalPrice.toLocaleString('en-IN')}
                                                        </div>
                                                    )}
                                                </div>

                                                <button
                                                    onClick={() => handleRemoveProduct(index)}
                                                    style={{
                                                        background: 'none', border: 'none',
                                                        cursor: 'pointer', padding: '6px 8px', borderRadius: 8,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        transition: 'all 0.2s', fontSize: 13
                                                    }}
                                                    onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; }}
                                                    onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                                                    title="Remove item"
                                                >
                                                    ❌
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* What's included (Subscription only) */}
                            {!isProductCheckout && (
                                <div style={{ marginTop: 20 }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 14 }}>
                                        What's included:
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                        {INCLUDED_DEFAULT.map((item, i) => (
                                            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                                <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>{item.icon}</span>
                                                <span style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.5 }}>{item.text}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* ── SHIPPING DETAILS FORM (Only for physical products) ── */}
                        {isProductCheckout && (
                            <div style={{
                                background: '#fff', borderRadius: 20, padding: '28px 28px',
                                border: '1.5px solid rgba(206,173,128,0.25)',
                                boxShadow: '0 4px 24px rgba(185,140,80,0.08)',
                            }}>
                                <h2 style={{ fontSize: 14, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 20 }}>
                                    🚚 Shipping Address
                                </h2>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                    <div>
                                        <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 6 }}>Full Name *</label>
                                        <input
                                            type="text"
                                            className="co-input"
                                            placeholder="Recipient's Name"
                                            value={shippingForm.fullName}
                                            onChange={e => setShippingForm({ ...shippingForm, fullName: e.target.value })}
                                            style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 14.5, boxSizing: 'border-box' }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 6 }}>Address Line 1 *</label>
                                        <input
                                            type="text"
                                            className="co-input"
                                            placeholder="House No, Flat, Street, Area"
                                            value={shippingForm.addressLine1}
                                            onChange={e => setShippingForm({ ...shippingForm, addressLine1: e.target.value })}
                                            style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 14.5, boxSizing: 'border-box' }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 6 }}>Address Line 2 (Optional)</label>
                                        <input
                                            type="text"
                                            className="co-input"
                                            placeholder="Landmark, Nearby location"
                                            value={shippingForm.addressLine2}
                                            onChange={e => setShippingForm({ ...shippingForm, addressLine2: e.target.value })}
                                            style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 14.5, boxSizing: 'border-box' }}
                                        />
                                    </div>
                                    <div className="ship-grid">
                                        <div>
                                            <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 6 }}>City *</label>
                                            <input
                                                type="text"
                                                className="co-input"
                                                placeholder="City"
                                                value={shippingForm.city}
                                                onChange={e => setShippingForm({ ...shippingForm, city: e.target.value })}
                                                style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 14.5, boxSizing: 'border-box' }}
                                            />
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 6 }}>State *</label>
                                            <input
                                                type="text"
                                                className="co-input"
                                                placeholder="State"
                                                value={shippingForm.state}
                                                onChange={e => setShippingForm({ ...shippingForm, state: e.target.value })}
                                                style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 14.5, boxSizing: 'border-box' }}
                                            />
                                        </div>
                                    </div>
                                    <div className="ship-grid">
                                        <div>
                                            <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 6 }}>PIN / Postal Code *</label>
                                            <input
                                                type="text"
                                                className="co-input"
                                                placeholder="6-digit ZIP code"
                                                value={shippingForm.postalCode}
                                                onChange={e => setShippingForm({ ...shippingForm, postalCode: e.target.value })}
                                                style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 14.5, boxSizing: 'border-box' }}
                                            />
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 6 }}>Contact Phone *</label>
                                            <input
                                                type="tel"
                                                className="co-input"
                                                placeholder="10-digit mobile number"
                                                value={shippingForm.phone}
                                                onChange={e => setShippingForm({ ...shippingForm, phone: e.target.value })}
                                                style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 14.5, boxSizing: 'border-box' }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ── Coupon Card ── */}
                        <div style={{
                            background: '#fff', borderRadius: 20, padding: '24px 28px',
                            border: '1.5px solid rgba(206,173,128,0.2)',
                            boxShadow: '0 4px 24px rgba(185,140,80,0.06)',
                        }}>
                            <h2 style={{ fontSize: 14, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 18 }}>
                                🎟 Coupon Code
                            </h2>

                            {!appliedCoupon ? (
                                <div>
                                    <div style={{ display: 'flex', gap: 10 }}>
                                        <div style={{ flex: 1, position: 'relative' }}>
                                            <input
                                                className="co-input"
                                                type="text"
                                                placeholder="Enter coupon code"
                                                value={couponInput}
                                                onChange={e => { setCouponInput(e.target.value.toUpperCase()); setCouponError(''); }}
                                                onKeyDown={e => e.key === 'Enter' && handleApplyCoupon()}
                                                style={{
                                                    width: '100%', padding: '13px 18px',
                                                    border: `1.5px solid ${couponError ? '#fca5a5' : '#e2e8f0'}`,
                                                    borderRadius: 12, fontSize: 15,
                                                    fontWeight: 700, letterSpacing: 1.2,
                                                    fontFamily: 'monospace',
                                                    color: '#1a0a00', background: '#fafafa',
                                                    boxSizing: 'border-box', transition: 'all 0.2s',
                                                    textTransform: 'uppercase',
                                                }}
                                            />
                                        </div>
                                        <button
                                            onClick={handleApplyCoupon}
                                            disabled={couponLoading || !couponInput.trim()}
                                            style={{
                                                padding: '13px 22px', borderRadius: 12, border: '1.5px solid #cead80',
                                                background: couponInput.trim() ? 'linear-gradient(135deg, #a07e4a, #cead80)' : '#f1f5f9',
                                                color: couponInput.trim() ? '#1a0a00' : '#94a3b8',
                                                fontWeight: 800, fontSize: 14, cursor: (!couponInput.trim() || couponLoading) ? 'not-allowed' : 'pointer',
                                                whiteSpace: 'nowrap', transition: 'all 0.2s',
                                                flexShrink: 0,
                                            }}
                                        >
                                            {couponLoading ? '...' : 'Apply'}
                                        </button>
                                    </div>
                                    {couponError && (
                                        <p style={{ fontSize: 13, color: '#ef4444', marginTop: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                                            ❌ {couponError}
                                        </p>
                                    )}
                                    <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>
                                        Case-insensitive · Applied discounts shown in order summary
                                    </p>
                                </div>
                            ) : (
                                <div style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '14px 18px', borderRadius: 12,
                                    background: 'linear-gradient(135deg, #dcfce7, #f0fdf4)',
                                    border: '1.5px solid #86efac',
                                }}>
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                                            <span style={{ fontFamily: 'monospace', fontWeight: 900, fontSize: 16, color: '#16a34a', letterSpacing: 1 }}>
                                                🎟 {appliedCoupon.code}
                                            </span>
                                            <span style={{ background: '#16a34a', color: '#fff', fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 50 }}>
                                                APPLIED
                                            </span>
                                        </div>
                                        <p style={{ fontSize: 13, color: '#15803d', fontWeight: 600 }}>
                                            You're saving ₹{appliedCoupon.savings.toLocaleString('en-IN')} with this coupon!
                                        </p>
                                    </div>
                                    <button onClick={handleRemoveCoupon} style={{
                                        background: 'rgba(22,163,74,0.1)', border: '1px solid #86efac',
                                        borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#16a34a',
                                        fontSize: 16, fontWeight: 700, lineHeight: 1,
                                    }}>✕</button>
                                </div>
                            )}
                        </div>

                        {/* ── Billing User Details Card ── */}
                        {user && (
                            <div style={{
                                background: '#fff', borderRadius: 20, padding: '20px 28px',
                                border: '1.5px solid rgba(206,173,128,0.15)',
                                boxShadow: '0 2px 12px rgba(185,140,80,0.05)',
                            }}>
                                <h2 style={{ fontSize: 14, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 14 }}>
                                    👤 Account Details
                                </h2>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                    {[
                                        { label: 'Account Name', value: `${user.firstName} ${user.lastName || ''}`.trim() },
                                        { label: 'Email', value: user.email },
                                        { label: 'Phone', value: user.phone || 'Not provided' },
                                        { label: 'Access Level', value: isProductCheckout ? '📦 Physical Products' : '♾️ Report Lifetime' },
                                    ].map(({ label, value }) => (
                                        <div key={label}>
                                            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 3 }}>{label}</div>
                                            <div style={{ fontSize: 14, color: '#1a0a00', fontWeight: 600 }}>{value}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ════════════ RIGHT — Order Summary ════════════ */}
                    <div className="co-summary-sticky" style={{ position: 'sticky', top: 90, display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>

                        {/* Summary Card */}
                        <div style={{
                            background: '#fff', borderRadius: 22, padding: '28px 26px',
                            border: '2px solid rgba(206,173,128,0.3)',
                            boxShadow: '0 8px 40px rgba(185,140,80,0.12)',
                        }}>
                            <h2 style={{ fontSize: 15, fontWeight: 800, color: '#1a0a00', marginBottom: 22 }}>
                                📋 Order Summary
                            </h2>

                            {/* Price breakdown */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontSize: 14, color: '#475569', whiteSpace: 'nowrap' }}>Original Price</span>
                                    <span style={{ fontSize: 14, color: '#94a3b8', textDecoration: 'line-through', whiteSpace: 'nowrap' }}>
                                        ₹{baseOriginalPrice.toLocaleString('en-IN')}
                                    </span>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontSize: 14, color: '#475569', whiteSpace: 'nowrap' }}>Special Discount</span>
                                    <span style={{ fontSize: 14, color: '#16a34a', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                        − ₹{(baseOriginalPrice - basePrice).toLocaleString('en-IN')}
                                    </span>
                                </div>

                                {appliedCoupon && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ fontSize: 14, color: '#475569' }}>Coupon <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: '#16a34a' }}>{appliedCoupon.code}</span></span>
                                        <span style={{ fontSize: 14, color: '#16a34a', fontWeight: 700 }}>
                                            − ₹{appliedCoupon.savings.toLocaleString('en-IN')}
                                        </span>
                                    </div>
                                )}

                                <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, #e2e8f0, transparent)', margin: '4px 0' }} />

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: 15, fontWeight: 800, color: '#1a0a00', whiteSpace: 'nowrap' }}>Total Payable</span>
                                    <span className="co-summary-price" style={{
                                        fontSize: 26, fontWeight: 900,
                                        background: 'linear-gradient(135deg, #a07e4a, #cead80)',
                                        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                                        transition: 'all 0.3s ease',
                                        whiteSpace: 'nowrap',
                                    }}>
                                        ₹{effectivePrice.toLocaleString('en-IN')}
                                    </span>
                                </div>

                                {/* Savings badge */}
                                <div style={{
                                    padding: '10px 14px', borderRadius: 12,
                                    background: 'linear-gradient(135deg, #dcfce7, #f0fdf4)',
                                    border: '1px solid #bbf7d0',
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    gap: 8, flexWrap: 'wrap',
                                }}>
                                    <span style={{ fontSize: 13, color: '#15803d', fontWeight: 600, whiteSpace: 'nowrap' }}>🎉 You're saving</span>
                                    <span className="co-savings-amount" style={{ fontSize: 15, fontWeight: 900, color: '#16a34a', whiteSpace: 'nowrap' }}>
                                        ₹{savings.toLocaleString('en-IN')} ({discountPct}%)
                                    </span>
                                </div>
                            </div>

                            {/* Pay Button */}
                            <button
                                id="checkout-pay-btn"
                                className="co-btn-pay"
                                onClick={handlePay}
                                disabled={paying}
                                style={{
                                    width: '100%', padding: '17px 24px',
                                    borderRadius: 16, border: 'none', cursor: paying ? 'not-allowed' : 'pointer',
                                    background: paying ? '#e2e8f0' : 'linear-gradient(135deg, #a07e4a 0%, #cead80 50%, #e8d4a8 100%)',
                                    color: paying ? '#94a3b8' : '#1a0a00',
                                    fontSize: 17, fontWeight: 900,
                                    boxShadow: paying ? 'none' : '0 8px 28px rgba(206,173,128,0.4)',
                                    transition: 'all 0.25s ease',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                                    letterSpacing: 0.3, marginBottom: 14,
                                }}
                            >
                                {paying ? (
                                    <>
                                        <span style={{ display: 'inline-block', animation: 'shimmer 1s ease-in-out infinite', fontSize: 18 }}>⏳</span>
                                        Processing…
                                    </>
                                ) : (
                                    <>🔒 Pay ₹{effectivePrice.toLocaleString('en-IN')} Securely</>
                                )}
                            </button>

                            {/* Security badges */}
                            <div style={{ display: 'flex', justifyContent: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                                {['UPI', 'Cards', 'Net Banking', 'Wallets'].map(m => (
                                    <span key={m} style={{ fontSize: 11, color: '#64748b', background: '#f8faff', border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 9px', fontWeight: 600 }}>
                                        {m}
                                    </span>
                                ))}
                            </div>

                            <div style={{ textAlign: 'center', fontSize: 12, color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                                🔐 Secured by <strong style={{ color: '#475569' }}>Razorpay</strong> · 256-bit SSL
                            </div>
                        </div>

                        {/* Trust row */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                            {[
                                { icon: '⚡', title: 'Express Delivery', sub: 'Packed with positive intent' },
                                { icon: '♾️', title: '100% Genuine', sub: 'Energised & authenticated' },
                                { icon: '📞', title: 'Support Included', sub: 'Dedicated support channel' },
                                { icon: '⭐', title: '4.9/5 Rating', sub: '2000+ happy buyers' },
                            ].map(({ icon, title, sub }) => (
                                <div key={title} style={{ background: '#fff', borderRadius: 12, padding: '12px 14px', border: '1.5px solid #f1f5f9', textAlign: 'center' }}>
                                    <div style={{ fontSize: 20, marginBottom: 4 }}>{icon}</div>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1a0a00' }}>{title}</div>
                                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{sub}</div>
                                </div>
                            ))}
                        </div>

                        <Link to="/dashboard" style={{ textAlign: 'center', fontSize: 13, color: '#94a3b8', textDecoration: 'none', display: 'block', paddingTop: 4 }}>
                            ← Back to Dashboard
                        </Link>
                    </div>

                </div>
            </div>
        </div>
    );
}
